/**
 * slotKit — box/line answer frames: the practice-frame foundation (CATALOG F2).
 *
 * Serves: fill-in blank · cloze + word bank · answer box (empty/lined/grid) ·
 * sentence starter frames · rubric rows · matching-column gutters.
 *
 * THE PRINT-KEY LAW: slots that can carry an answer take a `showAnswer`
 * flag. The answer text spec is ALWAYS emitted — visible:false when hidden —
 * so the answer survives in the design JSON and the teacher's "answer key"
 * print is one toggle, not a retype.
 *
 * Pure layout math only — see kits/specs.ts. Local origin (0,0 = top-left).
 */
import type { KitBlock, LineSpec, PrimitiveSpec, RectSpec, TextSpec, TextMeasurer } from './specs';
import { TEXT_BOX_HEIGHT } from './specs';

// ---------------------------------------------------------------------------
// Underline slot (fill-in blank)
// ---------------------------------------------------------------------------

export interface UnderlineSlotOptions {
  /** The answer that belongs on the line ('' = none stored yet). */
  answer: string;
  /** Print-key toggle — false hides the answer, keeps it in the JSON. */
  showAnswer: boolean;
  fontSize: number;
  color: string;
  /** Answer ink; defaults to `color` (teachers often want a key color). */
  answerColor?: string;
  fontFamily: string;
  measure: TextMeasurer;
  /** Fixed slot width. Omit for content-sized (see the sizing law). */
  width?: number;
  /** Content-sizing floor. Default fontSize * 2.2 — a blank is never a dot. */
  minWidth?: number;
  lineWeight?: number;
  /** Dblclick edit tag for the answer text. */
  answerParam?: string;
}

export interface UnderlineSlotBlock extends KitBlock {
  /** The rule's y — recipes sit neighbouring sentence text on this line. */
  lineY: number;
}

/**
 * The classic write-on line.
 *
 * THE CONTENT-OR-FIXED SIZING LAW: an explicit `width` wins (cloze lines a
 * teacher wants uniform); otherwise the line sizes to the stored answer —
 * max(minWidth, measure(answer) + one em of air) — so the key never
 * overflows its own blank.
 *
 * The rule sits at the text's box bottom; the answer (visible or not)
 * centers on the line.
 */
export function underlineSlot(opts: UnderlineSlotOptions): UnderlineSlotBlock {
  const fontSize = Math.max(1, opts.fontSize);
  const minWidth = opts.minWidth ?? fontSize * 2.2;
  const contentW = opts.answer ? opts.measure(opts.answer, fontSize) + fontSize : 0;
  const width = opts.width ?? Math.max(minWidth, contentW);
  const lineWeight = opts.lineWeight ?? Math.max(1.5, fontSize * 0.045);
  const boxH = fontSize * TEXT_BOX_HEIGHT;
  const lineY = boxH + lineWeight / 2;

  const answer: TextSpec = {
    kind: 'text',
    text: opts.answer,
    x: width / 2,
    y: 0,
    fontSize,
    fontFamily: opts.fontFamily,
    fill: opts.answerColor ?? opts.color,
    originX: 'center',
    originY: 'top',
    visible: opts.showAnswer,
    ...(opts.answerParam ? { param: opts.answerParam } : {}),
  };
  const rule: LineSpec = {
    kind: 'line',
    x1: 0,
    y1: lineY,
    x2: width,
    y2: lineY,
    stroke: opts.color,
    strokeWidth: lineWeight,
    lineCap: 'butt',
  };
  return { specs: [answer, rule], width, height: lineY + lineWeight / 2, lineY };
}

// ---------------------------------------------------------------------------
// Framed box (answer box)
// ---------------------------------------------------------------------------

export type BoxInterior = 'empty' | 'lined' | 'grid';

export interface FramedBoxOptions {
  width: number;
  height: number;
  interior: BoxInterior;
  /** Interior line pitch. Default height / 4. */
  spacing?: number;
  color: string;
  /** Interior guide ink; defaults to a job for the recipe (pass lighter). */
  guideColor?: string;
  strokeWidth?: number;
  guideWeight?: number;
  cornerRadius?: number;
  /** Panel fill behind the writing area ('' = transparent). */
  fill?: string;
}

/**
 * A framed writing area with an empty, lined, or grid interior.
 *
 * THE INTERIOR LAW: guide lines live STRICTLY INSIDE the frame — first line
 * one `spacing` in, none touching an edge (a guide overlapping the frame
 * reads as a double-struck border in print). Lined = horizontals only;
 * grid adds verticals at the same pitch.
 */
export function framedBox(opts: FramedBoxOptions): KitBlock {
  const { width, height } = opts;
  const spacing = Math.max(4, opts.spacing ?? height / 4);
  const strokeWidth = opts.strokeWidth ?? 2.5;
  const guideWeight = opts.guideWeight ?? Math.max(1, strokeWidth * 0.45);
  const guideColor = opts.guideColor ?? opts.color;

  const frame: RectSpec = {
    kind: 'rect',
    x: 0,
    y: 0,
    width,
    height,
    fill: opts.fill ?? '',
    stroke: opts.color,
    strokeWidth,
    rx: opts.cornerRadius ?? 0,
  };
  const specs: PrimitiveSpec[] = [frame];

  const guide = (x1: number, y1: number, x2: number, y2: number): LineSpec => ({
    kind: 'line',
    x1,
    y1,
    x2,
    y2,
    stroke: guideColor,
    strokeWidth: guideWeight,
    lineCap: 'butt',
  });

  if (opts.interior === 'lined' || opts.interior === 'grid') {
    for (let y = spacing; y < height - guideWeight; y += spacing) {
      specs.push(guide(0, y, width, y));
    }
  }
  if (opts.interior === 'grid') {
    for (let x = spacing; x < width - guideWeight; x += spacing) {
      specs.push(guide(x, 0, x, height));
    }
  }
  return { specs, width, height };
}

// ---------------------------------------------------------------------------
// Word bank
// ---------------------------------------------------------------------------

export interface WordBankOptions {
  title: string;
  words: string[];
  /** Total box width — the wrap constraint. */
  width: number;
  fontSize: number;
  /** Title size. Default fontSize * 0.78, bold. */
  titleSize?: number;
  color: string;
  titleColor?: string;
  fontFamily: string;
  measure: TextMeasurer;
  /** Interior padding. Default fontSize * 0.6. */
  pad?: number;
  /** Horizontal air between chips. Default fontSize * 1.1. */
  chipGap?: number;
  strokeWidth?: number;
  cornerRadius?: number;
}

export interface WordBankBlock extends KitBlock {
  /** Rows the words wrapped into (test surface for the wrap law). */
  rowsUsed: number;
}

/**
 * Word-bank box: a titled frame with the choice words flowed inside as
 * chip texts.
 *
 * THE WRAP LAW: words flow left → right in given order and wrap when the
 * next word would cross `width - pad`; a word longer than the interior gets
 * its own row rather than escaping the frame. The box's height is derived
 * from the rows used — the frame always contains its words by construction.
 */
export function wordBank(opts: WordBankOptions): WordBankBlock {
  const fontSize = Math.max(1, opts.fontSize);
  const titleSize = opts.titleSize ?? fontSize * 0.78;
  const pad = opts.pad ?? fontSize * 0.6;
  const chipGap = opts.chipGap ?? fontSize * 1.1;
  const rowPitch = fontSize * TEXT_BOX_HEIGHT * 1.18;
  const innerW = Math.max(fontSize, opts.width - 2 * pad);

  const specs: PrimitiveSpec[] = [];
  const title: TextSpec = {
    kind: 'text',
    text: opts.title,
    x: pad,
    y: pad,
    fontSize: titleSize,
    fontFamily: opts.fontFamily,
    fill: opts.titleColor ?? opts.color,
    fontWeight: 'bold',
    originX: 'left',
    originY: 'top',
  };
  specs.push(title);

  let x = pad;
  let y = pad + titleSize * TEXT_BOX_HEIGHT + fontSize * 0.45;
  let rowsUsed = opts.words.length > 0 ? 1 : 0;
  for (const word of opts.words) {
    const w = opts.measure(word, fontSize);
    if (x > pad && x + w > pad + innerW) {
      x = pad;
      y += rowPitch;
      rowsUsed += 1;
    }
    const chip: TextSpec = {
      kind: 'text',
      text: word,
      x,
      y,
      fontSize,
      fontFamily: opts.fontFamily,
      fill: opts.color,
      originX: 'left',
      originY: 'top',
    };
    specs.push(chip);
    x += w + chipGap;
  }
  const height =
    (opts.words.length > 0 ? y + fontSize * TEXT_BOX_HEIGHT : y) + pad;

  const frame: RectSpec = {
    kind: 'rect',
    x: 0,
    y: 0,
    width: opts.width,
    height,
    fill: '',
    stroke: opts.color,
    strokeWidth: opts.strokeWidth ?? 2.5,
    rx: opts.cornerRadius ?? fontSize * 0.35,
  };
  specs.unshift(frame); // frame paints first, words on top
  return { specs, width: opts.width, height, rowsUsed };
}
